import axios from 'axios';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    const fallbackToSavedAddress = (errorMsg) => {
      // Amazon-style fallback: If location is blocked/fails, check saved user address in localStorage
      try {
        const stored = localStorage.getItem('userFullAddress');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.coordinates && parsed.coordinates.length === 2) {
            return resolve({
              lat: parsed.coordinates[1],
              lng: parsed.coordinates[0],
              fromSavedAddress: true,
              address: parsed.formattedAddress || parsed.address
            });
          }
        }
      } catch (e) {
        console.error("Error reading saved address fallback:", e);
      }
      reject(new Error(errorMsg || 'Geolocation failed and no saved address found.'));
    };

    if (!navigator.geolocation) {
      return fallbackToSavedAddress('Geolocation is not supported by your browser.');
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          fromSavedAddress: false
        });
      },
      (error) => {
        console.warn('Geolocation error/blocked:', error.message);
        fallbackToSavedAddress(error.message);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
};

export const getAddressFromCoords = async (lat, lng) => {
  try {
    const response = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
    );

    if (response.data.status === 'OK' && response.data.results.length > 0) {
      const result = response.data.results[0];
      
      // Extract components
      const addressComponents = result.address_components;
      const getComponent = (type, nameType = 'long_name') => {
        const component = addressComponents.find(c => c.types.includes(type));
        return component ? component[nameType] : '';
      };

      const city = getComponent('locality') || getComponent('administrative_area_level_2');
      const state = getComponent('administrative_area_level_1');
      const country = getComponent('country');
      const countryCode = getComponent('country', 'short_name');
      const pincode = getComponent('postal_code');
      const street = getComponent('route') || getComponent('sublocality_level_1') || getComponent('sublocality');
      const houseNumber = getComponent('street_number');

      return {
        formattedAddress: result.formatted_address,
        city,
        state,
        country,
        countryCode,
        postalCode: pincode,
        pincode,
        street,
        houseNumber,
        coordinates: [lng, lat], // [lng, lat] for GeoJSON
        lat,
        lng,
      };
    } else {
      throw new Error(response.data.error_message || 'Failed to fetch address');
    }
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    throw error;
  }
};

// Saves the chosen delivery point where the home page, restaurant pages and checkout read it.
export const saveDeliveryLocation = async (lat, lng, formattedAddr = null) => {
  const addressData = await getAddressFromCoords(lat, lng);
  const fullAddr = formattedAddr || addressData.formattedAddress;
  const shortAddress = fullAddr.split(',')[0] + ', ' + (addressData.city || '');
  localStorage.setItem('userLocation', shortAddress);
  localStorage.setItem('userFullAddress', JSON.stringify({
    ...addressData,
    formattedAddress: fullAddr,
    coordinates: [lng, lat],
  }));
  return { shortAddress, city: addressData.city, lat, lng };
};
